import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Card } from "react-bootstrap";
import { getIndicators } from "../services/api";
import MarketStateBadges from "./MarketStateBadges";
import "../styles/Indicators.css";

const AUTO_REFRESH_MS = 10000;
const FALLBACK_SYMBOL = "BTCUSDT";

const formatSymbol = (symbol) => {
  if (!symbol) return "BTC_USDT";
  if (symbol.includes("_")) return symbol.toUpperCase();
  return symbol.toUpperCase().replace("USDT", "_USDT");
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric !== null) return numeric;
  }
  return null;
};

const formatFixed = (value, digits = 2) => {
  const numeric = toNumber(value);
  return numeric === null ? "N/A" : numeric.toFixed(digits);
};

const formatCurrency = (value, digits = 4) => {
  const numeric = toNumber(value);
  return numeric === null ? "N/A" : `$${numeric.toFixed(digits)}`;
};

const formatSignedPercent = (value, digits = 2) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(digits)}%`;
};

const formatCompact = (value, digits = 2) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  if (Math.abs(numeric) >= 1_000_000) {
    return `${(numeric / 1_000_000).toFixed(digits)}M`;
  }
  if (Math.abs(numeric) >= 1_000) {
    return `${(numeric / 1_000).toFixed(digits)}k`;
  }
  return numeric.toFixed(digits);
};

const getRsiMeta = (rsiValue) => {
  const rsi = toNumber(rsiValue);
  if (rsi === null) {
    return { label: "Sin dato", tone: "neutral" };
  }
  if (rsi >= 70) return { label: "Sobrecomprado", tone: "negative" };
  if (rsi >= 55) return { label: "Impulso alcista", tone: "positive" };
  if (rsi >= 45) return { label: "Equilibrado", tone: "warning" };
  if (rsi >= 30) return { label: "Debilidad", tone: "negative" };
  return { label: "Sobrevendido", tone: "warning" };
};

const getTrendMeta = (trendRaw) => {
  const trend = String(trendRaw || "").toUpperCase();
  if (!trend) return { label: "Desconocida", tone: "neutral" };
  if (trend.includes("ALCISTA FUERTE")) return { label: "Alcista fuerte", tone: "positive" };
  if (trend.includes("ALCISTA")) return { label: "Alcista", tone: "positive" };
  if (trend.includes("BAJISTA FUERTE")) return { label: "Bajista fuerte", tone: "negative" };
  if (trend.includes("BAJISTA")) return { label: "Bajista", tone: "negative" };
  return { label: "Neutral", tone: "warning" };
};

const getMacdTone = (macd, signal) => {
  if (macd === null || signal === null) return "neutral";
  return macd >= signal ? "positive" : "negative";
};

const getSignedTone = (value) => {
  const numeric = toNumber(value);
  if (numeric === null) return "neutral";
  if (numeric > 0) return "positive";
  if (numeric < 0) return "negative";
  return "warning";
};

const calculateScore = (rawIndicators) => {
  if (!rawIndicators || typeof rawIndicators !== "object") return 0;

  let weightedSum = 0;
  let totalWeight = 0;

  const rsi = toNumber(rawIndicators.RSI);
  if (rsi !== null) {
    let rsiScore = 50;
    if (rsi < 30) rsiScore = 78;
    else if (rsi < 45) rsiScore = 58;
    else if (rsi <= 65) rsiScore = 86;
    else if (rsi <= 72) rsiScore = 66;
    else rsiScore = 34;

    weightedSum += rsiScore * 0.24;
    totalWeight += 0.24;
  }

  const macd = toNumber(rawIndicators.MACD);
  const macdSignal = toNumber(rawIndicators.MACD_signal);
  if (macd !== null && macdSignal !== null) {
    const gap = macd - macdSignal;
    let macdScore = 45;
    if (gap > 0.002) macdScore = 92;
    else if (gap > 0) macdScore = 76;
    else if (gap > -0.002) macdScore = 42;
    else macdScore = 18;

    weightedSum += macdScore * 0.23;
    totalWeight += 0.23;
  }

  const trend = getTrendMeta(rawIndicators.trend);
  if (trend.tone !== "neutral") {
    const trendScore = trend.tone === "positive" ? 88 : 20;
    weightedSum += trendScore * 0.28;
    totalWeight += 0.28;
  }

  const volume = toNumber(rawIndicators.volume);
  const volumeMa = toNumber(rawIndicators.volume_ma);
  if (volume !== null && volumeMa !== null && volumeMa > 0) {
    const ratio = volume / volumeMa;
    let volumeScore = 40;
    if (ratio >= 1.8) volumeScore = 94;
    else if (ratio >= 1.3) volumeScore = 82;
    else if (ratio >= 1.0) volumeScore = 68;
    else if (ratio >= 0.8) volumeScore = 46;
    else volumeScore = 24;

    weightedSum += volumeScore * 0.25;
    totalWeight += 0.25;
  }

  return totalWeight > 0 ? weightedSum / totalWeight : 0;
};

const Indicators = ({
  symbol = FALLBACK_SYMBOL,
  refreshTrigger = 0,
  manualRefreshTrigger = 0,
  availablePairs = [],
  onSymbolChange,
}) => {
  const [indicators, setIndicators] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorEstado, setErrorEstado] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  const lastRefreshRef = useRef(0);
  const requestSeqRef = useRef(0);
  const currentSymbolRef = useRef("");

  const currentSymbol = useMemo(() => formatSymbol(symbol || FALLBACK_SYMBOL), [symbol]);

  useEffect(() => {
    currentSymbolRef.current = currentSymbol;
    setIndicators(null);
    setError(null);
    setErrorEstado(null);
    setLoading(true);
  }, [currentSymbol]);

  const fetchIndicators = useCallback(
    async (showLoading = false) => {
      const requestSymbol = currentSymbol;
      const requestSeq = requestSeqRef.current + 1;
      requestSeqRef.current = requestSeq;

      try {
        if (showLoading) setLoading(true);

        const response = await getIndicators(requestSymbol);
        if (requestSeq !== requestSeqRef.current || requestSymbol !== currentSymbolRef.current) {
          return;
        }
        if (!Number.isFinite(Number(response?.close))) {
          throw new Error("Indicadores sin precio valido (close)");
        }

        setIndicators(response);
        setLastUpdate(new Date());
        setError(null);
        setErrorEstado(null);
      } catch (err) {
        if (requestSeq !== requestSeqRef.current || requestSymbol !== currentSymbolRef.current) {
          return;
        }
        console.error("❌ Error al obtener indicadores:", err);
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          "Error al obtener indicadores";
        setError(message);
        setErrorEstado(err?.estadoMercado || null);
      } finally {
        if (showLoading && requestSeq === requestSeqRef.current && requestSymbol === currentSymbolRef.current) {
          setLoading(false);
        }
      }
    },
    [currentSymbol]
  );

  useEffect(() => {
    fetchIndicators(true);
  }, [fetchIndicators]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      const now = Date.now();
      if (now - lastRefreshRef.current < AUTO_REFRESH_MS * 0.8) return;
      lastRefreshRef.current = now;
      fetchIndicators(false);
    }
  }, [refreshTrigger, fetchIndicators]);

  useEffect(() => {
    if (manualRefreshTrigger > 0) {
      lastRefreshRef.current = Date.now();
      fetchIndicators(false);
    }
  }, [manualRefreshTrigger, fetchIndicators]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchIndicators(false);
    }, AUTO_REFRESH_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchIndicators]);

  const viewModel = useMemo(() => {
    if (!indicators) return null;

    const close = toNumber(indicators.close);
    const open = toNumber(indicators.open);
    const high = toNumber(indicators.high);
    const low = toNumber(indicators.low);
    const min24h = firstNumber(indicators.min_24h, indicators.min_1h_5m);
    const max24h = firstNumber(indicators.max_24h, indicators.max_1h_5m);
    const range24hPct =
      close && min24h !== null && max24h !== null && max24h > min24h
        ? ((max24h - min24h) / close) * 100
        : null;

    const priceChangePct =
      toNumber(indicators.precio_cambio_pct) ??
      (close && open ? ((close - open) / open) * 100 : null);

    const rsi = toNumber(indicators.RSI);
    const rsiPrev = toNumber(indicators.RSI_prev);
    const rsiDelta =
      rsi !== null && rsiPrev !== null ? rsi - rsiPrev : null;
    const rsiMeta = getRsiMeta(rsi);

    const trend = indicators.trend || "N/A";
    const trendMeta = getTrendMeta(trend);

    const macd = toNumber(indicators.MACD);
    const macdSignal = toNumber(indicators.MACD_signal);
    const macdTone = getMacdTone(macd, macdSignal);
    const macdGap =
      macd !== null && macdSignal !== null ? macd - macdSignal : null;

    const macdPrev = toNumber(indicators.MACD_prev);
    const macdSignalPrev = toNumber(indicators.MACD_signal_prev);
    const macdGapPrev =
      macdPrev !== null && macdSignalPrev !== null
        ? macdPrev - macdSignalPrev
        : null;
    const macdGapDelta =
      macdGap !== null && macdGapPrev !== null ? macdGap - macdGapPrev : null;

    const cci = toNumber(indicators.CCI_20);
    const atr = toNumber(indicators.ATR);
    const atrPct = close && atr !== null ? (atr / close) * 100 : null;

    const volume = toNumber(indicators.volume);
    const volumeMa = toNumber(indicators.volume_ma);
    const volumeRatio =
      toNumber(indicators.volume_ratio) ??
      (volume !== null && volumeMa && volumeMa > 0 ? volume / volumeMa : null);

    const bbUpper = firstNumber(
      indicators.BB_upper_1h,
      indicators.BB_upper,
      indicators.bb_upper
    );
    const bbLower = firstNumber(
      indicators.BB_lower_1h,
      indicators.BB_lower,
      indicators.bb_lower
    );
    const bbWidthPct =
      close && bbUpper !== null && bbLower !== null && bbUpper > bbLower
        ? ((bbUpper - bbLower) / close) * 100
        : null;
    const bbHeadroomPct =
      close && bbUpper !== null ? ((bbUpper - close) / close) * 100 : null;
    const bbFloorDistancePct =
      close && bbLower !== null ? ((close - bbLower) / close) * 100 : null;

    const balanceForBuy = toNumber(indicators.balance_for_buy);
    const orderValue = toNumber(indicators.valor_orden);

    const heroCards = [
      {
        key: "price",
        title: "💰 Precio",
        value: formatCurrency(close, 4),
        hint: `${formatCurrency(low, 4)} - ${formatCurrency(high, 4)}`,
        tone: getSignedTone(priceChangePct),
        badge: formatSignedPercent(priceChangePct, 2),
      },
      {
        key: "rsi",
        title: "🔴 RSI",
        value: formatFixed(rsi, 2),
        hint: rsiMeta.label,
        tone: rsiMeta.tone,
        badge: formatSignedPercent(rsiDelta, 2),
      },
      {
        key: "trend",
        title: "📈 Tendencia",
        value: trend,
        hint: trendMeta.label,
        tone: trendMeta.tone,
        badge: indicators.tendencia_5m || "N/A",
      },
      {
        key: "volume",
        title: "📊 Volumen",
        value: `${formatCompact(volume, 0)} (${formatFixed(volumeRatio, 2)}x)`,
        hint: `MA ${formatCompact(volumeMa, 0)}`,
        tone:
          volumeRatio !== null && volumeRatio >= 1
            ? "positive"
            : volumeRatio !== null
            ? "warning"
            : "neutral",
        badge: `ratio ${formatFixed(volumeRatio, 2)}`,
      },
    ];

    const panels = [
      {
        title: "Momentum",
        icon: "⚡",
        metrics: [
          {
            label: "MACD / Signal",
            value: `${formatFixed(macd, 6)} / ${formatFixed(macdSignal, 6)}`,
            hint: `Gap ${formatFixed(macdGap, 6)}`,
            tone: macdTone,
          },
          {
            label: "Gap MACD Δ",
            value: formatFixed(macdGapDelta, 6),
            hint: `Prev ${formatFixed(macdGapPrev, 6)}`,
            tone: getSignedTone(macdGapDelta),
          },
          {
            label: "CCI 20",
            value: formatFixed(cci, 2),
            hint: cci !== null && cci >= 100 ? "Sobre compra" : cci !== null && cci <= -100 ? "Sobre venta" : "Zona media",
            tone: getSignedTone(cci),
          },
          {
            label: "RSI Δ",
            value: formatSignedPercent(rsiDelta, 2),
            hint: `RSI Prev ${formatFixed(rsiPrev, 2)}`,
            tone: getSignedTone(rsiDelta),
          },
        ],
      },
      {
        title: "Volatilidad y Bandas",
        icon: "🛰️",
        metrics: [
          {
            label: "ATR",
            value: formatCurrency(atr, 4),
            hint: `ATR% ${formatSignedPercent(atrPct, 2)}`,
            tone: atrPct !== null && atrPct > 1 ? "warning" : "neutral",
          },
          {
            label: "BB Superior",
            value: formatCurrency(bbUpper, 4),
            hint: `Headroom ${formatSignedPercent(bbHeadroomPct, 2)}`,
            tone: getSignedTone(bbHeadroomPct),
          },
          {
            label: "BB Inferior",
            value: formatCurrency(bbLower, 4),
            hint: `Distancia ${formatSignedPercent(bbFloorDistancePct, 2)}`,
            tone: getSignedTone(bbFloorDistancePct),
          },
          {
            label: "Ancho BB",
            value: formatSignedPercent(bbWidthPct, 2),
            hint: "(BB upper - BB lower) / precio",
            tone: bbWidthPct !== null && bbWidthPct >= 1 ? "warning" : "neutral",
          },
        ],
      },
      {
        title: "Rango y Sesgo",
        icon: "🧭",
        metrics: [
          {
            label: "Rango 24H",
            value: `${formatCurrency(min24h, 4)} - ${formatCurrency(max24h, 4)}`,
            hint: `Ancho ${formatSignedPercent(range24hPct, 2)}`,
            tone: range24hPct !== null && range24hPct >= 1 ? "warning" : "neutral",
          },
          {
            label: "Tendencia 5M / 1H",
            value: `${indicators.tendencia_5m || "N/A"} | ${indicators.tendencia_1h || "N/A"}`,
            hint: `2H ${indicators.tendencia_2h || "N/A"}`,
            tone: getTrendMeta(indicators.tendencia_5m).tone,
          },
          {
            label: "EMA 5 / 20",
            value: `${formatCurrency(indicators.EMA_5, 4)} / ${formatCurrency(indicators.EMA_20, 4)}`,
            hint: "Cruce de medias",
            tone:
              toNumber(indicators.EMA_5) !== null &&
              toNumber(indicators.EMA_20) !== null &&
              toNumber(indicators.EMA_5) >= toNumber(indicators.EMA_20)
                ? "positive"
                : "negative",
          },
          {
            label: "Cambio de precio",
            value: formatSignedPercent(priceChangePct, 3),
            hint: `Open ${formatCurrency(open, 4)} | Close ${formatCurrency(close, 4)}`,
            tone: getSignedTone(priceChangePct),
          },
        ],
      },
      {
        title: "Capital y Orden",
        icon: "💼",
        metrics: [
          {
            label: "Balance disponible",
            value: formatCurrency(balanceForBuy, 2),
            hint: "USDT libre para compra",
            tone: "positive",
          },
          {
            label: "Valor de orden",
            value: formatCurrency(orderValue, 2),
            hint: "Configuracion actual",
            tone:
              balanceForBuy !== null && orderValue !== null && orderValue <= balanceForBuy
                ? "positive"
                : "warning",
          },
          {
            label: "Volume / MA",
            value: `${formatCompact(volume, 2)} / ${formatCompact(volumeMa, 2)}`,
            hint: `Ratio ${formatFixed(volumeRatio, 2)}x`,
            tone:
              volumeRatio !== null && volumeRatio >= 1.2
                ? "positive"
                : volumeRatio !== null && volumeRatio >= 0.9
                ? "warning"
                : "negative",
          },
          {
            label: "Ultima vela",
            value: `${formatCurrency(low, 4)} - ${formatCurrency(high, 4)}`,
            hint: `Cierre ${formatCurrency(close, 4)}`,
            tone: "neutral",
          },
        ],
      },
    ];

    return {
      rsi,
      macdGap,
      heroCards,
      panels,
    };
  }, [indicators]);

  const score = useMemo(() => calculateScore(indicators), [indicators]);
  const recommendationClass =
    score >= 72 ? "comprar" : score >= 52 ? "neutral" : "vender";
  const recommendationLabel =
    score >= 72 ? "COMPRAR" : score >= 52 ? "ESPERAR" : "VENDER";

  if (loading && !indicators) {
    return (
      <Card className="indicators-card-wrapper">
        <Card.Header className="indicators-card-header">
          <h5 className="indicators-card-title">📊 Indicadores - {currentSymbol}</h5>
        </Card.Header>
        <Card.Body className="indicators-card-body">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>⏳ Cargando indicadores...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error && !indicators) {
    return (
      <Card className="indicators-card-wrapper">
        <Card.Header className="indicators-card-header">
          <h5 className="indicators-card-title">📊 Indicadores - {currentSymbol}</h5>
        </Card.Header>
        <Card.Body className="indicators-card-body">
          <div className="error-state">
            <p>❌ {error}</p>
            <button onClick={() => fetchIndicators(true)} className="btn-retry">
              🔄 Reintentar
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!indicators || !viewModel) {
    return (
      <Card className="indicators-card-wrapper">
        <Card.Header className="indicators-card-header">
          <h5 className="indicators-card-title">📊 Indicadores - {currentSymbol}</h5>
        </Card.Header>
        <Card.Body className="indicators-card-body">
          <p className="text-center">❌ No hay datos disponibles</p>
          <MarketStateBadges estado={errorEstado} />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="indicators-card-wrapper">
      <Card.Header className="indicators-card-header">
        <div className="indicators-header-content">
          <h5 className="indicators-card-title">📊 Indicadores - {currentSymbol}</h5>
          {availablePairs.length > 1 ? (
            <select
              className="indicator-symbol-select"
              value={currentSymbol}
              onChange={(event) => onSymbolChange?.(event.target.value)}
            >
              {availablePairs.map((pair) => (
                <option key={`${pair.market_type}:${pair.symbol}`} value={pair.symbol}>
                  {pair.symbol} · {pair.market_type === "FUTURES" ? "Futuros" : "Spot"}
                </option>
              ))}
            </select>
          ) : null}
          <span className="update-time">
            🔄 Actualizado: {lastUpdate?.toLocaleTimeString() || "..."}
          </span>
        </div>
        <MarketStateBadges estado={indicators?.estado_mercado} />
      </Card.Header>

      <Card.Body className="indicators-card-body">
        {error && (
          <div className="indicators-inline-error">⚠️ Última consulta con error: {error}</div>
        )}

        <div className={`score-card score-${recommendationClass}`}>
          <div className="score-circle">
            <div className="score-number">{score.toFixed(1)}</div>
            <div className="score-slash">/100</div>
          </div>

          <div className="score-content">
            <p className="score-title">Puntuación compuesta</p>
            <p className={`score-recommendation score-recommendation-${recommendationClass}`}>
              {recommendationLabel}
            </p>

            <div className="score-chips">
              <span className={`score-chip tone-${getRsiMeta(viewModel.rsi).tone}`}>
                RSI {formatFixed(viewModel.rsi, 2)}
              </span>
              <span className={`score-chip tone-${getSignedTone(viewModel.macdGap)}`}>
                MACD gap {formatFixed(viewModel.macdGap, 6)}
              </span>
            </div>
          </div>
        </div>

        <div className="indicator-hero-grid">
          {viewModel.heroCards.map((card) => (
            <div key={card.key} className={`indicator-hero-card tone-${card.tone}`}>
              <span className="indicator-hero-label">{card.title}</span>
              <span className="indicator-hero-value">{card.value}</span>
              <span className="indicator-hero-hint">{card.hint}</span>
              <span className="indicator-hero-badge">{card.badge}</span>
            </div>
          ))}
        </div>

        <div className="indicator-panel-grid">
          {viewModel.panels.map((panel) => (
            <section key={panel.title} className="indicator-panel">
              <header className="indicator-panel-header">
                <span className="indicator-panel-title">
                  {panel.icon} {panel.title}
                </span>
              </header>

              <div className="indicator-metric-list">
                {panel.metrics.map((metric) => (
                  <div key={`${panel.title}-${metric.label}`} className="indicator-metric">
                    <div className="indicator-metric-label">{metric.label}</div>
                    <div className={`indicator-metric-value tone-${metric.tone || "neutral"}`}>
                      {metric.value}
                    </div>
                    <div className="indicator-metric-hint">{metric.hint}</div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export default React.memo(Indicators);
